/**
 * AdminFirmsLanding — the firm-first admin landing (spec §1/§2). Platform admin
 * lands here: a list of every firm (with side badge + case count), then a
 * Golden Cases section (demo/reference cases, not tied to a firm). Clicking a
 * firm drills into its cases (firm → cases → seat lens); the drill-down page is
 * built in the next slice, so rows route to /admin/firms/:id.
 *
 * Reuses GET /api/v1/law-firms (already returns name, cases_count, client_side).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Search, Building2, Star, ChevronRight } from 'lucide-react';
import { useAuth } from '../../contexts/TestAuthContext';
import { api } from '../../utils/api';
import AppLayout from '../../components/AppLayout';

interface LawFirm {
  id: string;
  name: string;
  cases_count?: number;
  client_side?: string | null;
}

const fmtSide = (s?: string | null) => (s === 'carrier' ? 'carrier' : null);

export default function AdminFirmsLanding() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [firms, setFirms] = useState<LawFirm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getToken();
        const data = await api.get<{ law_firms: LawFirm[] }>('/api/v1/law-firms', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (active) setFirms(data?.law_firms || []);
      } catch (e: any) {
        if (active) setError(e?.message || 'Could not load firms.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [getToken]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const sorted = [...firms].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    if (!needle) return sorted;
    return sorted.filter((f) => (f.name || '').toLowerCase().includes(needle));
  }, [firms, q]);

  return (
    <AppLayout>
    <div className="max-w-5xl mx-auto space-y-8 p-6">
      <div>
        <h1 className="cs-h1" style={{ color: 'var(--color-text-primary)' }}>Firms</h1>
        <p className="cs-meta mt-1" style={{ color: 'var(--color-text-muted)' }}>
          Pick a firm → its cases → open one → flip the seat lens.
        </p>
      </div>

      {/* Search */}
      <div className="relative" style={{ maxWidth: 340 }}>
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-dim)' }} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search firms…"
          className="pl-9 pr-3 py-2 rounded-lg text-sm w-full"
          style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--color-text-muted)' }} />
        </div>
      ) : error ? (
        <p className="py-10 text-center text-sm" style={{ color: 'var(--color-needs-human-text)' }}>{error}</p>
      ) : (
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg-surface)' }}>
          {filtered.length === 0 ? (
            <p className="py-12 text-center text-sm" style={{ color: 'var(--color-text-muted)' }}>
              {firms.length === 0 ? 'No firms yet.' : 'No firms match your search.'}
            </p>
          ) : (
            filtered.map((f, i) => (
              <button
                key={f.id}
                type="button"
                onClick={() => navigate(`/admin/firms/${f.id}`)}
                className="w-full flex items-center gap-4 px-5 py-4 text-left transition-colors hover:brightness-110"
                style={{ borderTop: i ? '1px solid var(--color-border)' : 'none', background: 'transparent' }}
              >
                <Building2 className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--color-text-dim)' }} />
                <div className="min-w-0" style={{ flex: '0 0 auto', minWidth: 220 }}>
                  <div className="flex items-center gap-2">
                    <span style={{ color: 'var(--color-text-primary)', fontWeight: 600 }}>{f.name}</span>
                    {fmtSide(f.client_side) && (
                      <span className="font-mono text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded"
                        style={{ color: 'var(--color-needs-human-text)', border: '1px solid var(--color-needs-human-text)' }}>
                        carrier
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex-1 text-right text-sm font-mono" style={{ color: 'var(--color-text-muted)' }}>
                  {typeof f.cases_count === 'number' ? `${f.cases_count} case${f.cases_count === 1 ? '' : 's'}` : ''}
                </div>
                <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-text-dim)' }} />
              </button>
            ))
          )}
        </div>
      )}

      {/* Golden Cases — top-level, not tied to a firm (data wiring: next slice) */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Star className="w-4 h-4" style={{ color: 'var(--color-needs-human-text)' }} />
          <span className="font-mono text-xs uppercase tracking-wide" style={{ color: 'var(--color-text-secondary)' }}>Golden Cases</span>
          <span className="text-xs" style={{ color: 'var(--color-text-dim)' }}>· demo + reference · seat lens works identically</span>
        </div>
        <button
          type="button"
          onClick={() => navigate('/admin/golden-cases')}
          className="rounded-xl px-5 py-4 text-sm flex items-center justify-between w-full text-left"
          style={{ border: '1px dashed var(--color-border)', color: 'var(--color-text-muted)', background: 'transparent' }}
        >
          <span>Open Golden Cases</span>
          <ChevronRight className="w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
        </button>
      </div>
    </div>
    </AppLayout>
  );
}
